/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */

/**
 * The component to show circle diagram for widget
 *
 * @author Yakov Gushchin
 * @author Maxim Fedorov
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.0.1
 */

import { Fragment, memo, useEffect, useRef, useState } from 'react';
import type { FC } from 'react';

import debounce from 'lodash/debounce';
import get from 'lodash/get';

import { formatBalance } from '@utils/formatText';

import BizCharts from '../BizchartsLoadable';
import ChartLegend from './ChartLegend';
import { IChartPieItem, IPieChartProps } from './chartPieTypes';
import ChartTooltip from './ChartTooltip';
import { ChartContainerStyled, ChartHolderStyled } from './styled/ChartPieStyled';

// if total < BigValue use big font and vice versa
const BigValue = 10000000;
const PieChartTitle = (total: number, currency: string): string =>
    `<div style="text-align:center;">
        <span class="${total < BigValue ? 'chart__title' : 'chart__title_small'}" role='heading'>
            ${formatBalance(total)} ${currency}
        </span>
    </div>`;

export const PieChart: FC<IPieChartProps> = props => {

    const chartLegendRef = useRef<ChartLegend>(null);
    const chartRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<ChartTooltip>(null);

    const {
        children,
        currency,
        data: chartData,
        innerRadius,
        isEmpty,
        legendSize,
        padding,
        radius,
        size,
        strokeColor,
    } = props;

    const [data, setData] = useState<IChartPieItem[]>([]);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        let chartTotal = 0;
        const safeData = chartData.filter(item => item.value > 0);
        safeData.forEach(item => {
            if (item.value > 0) {
                chartTotal += item.value;
            }
        });
        if (chartTotal) {
            setData(safeData.filter(item => item.value)
                .filter(item => item.value)
                .map(item => ({
                    ...item,
                    value: item.value / chartTotal > .01 ? item.value : chartTotal / 100,
                })),
            );
            if (!isEmpty) {
                setTotal(chartTotal);
            }
        }
    }, [chartData, isEmpty]);

    const drop: number = data.length > legendSize ? data.length - legendSize : 0;
    const onTooltipHide = (): void => {
        if (tooltipRef.current) {
            tooltipRef.current.setState({ isVisible: false });
        }
        if (chartLegendRef.current) {
            chartLegendRef.current.setState({ isTooltipVisible: false });
        }
    };

    const onPlotMove = (ev: G2.EventParams): void => {
        if (tooltipRef.current && ev.x && ev.y) {
            const item = {
                ...tooltipRef.current.state.item,
                x: ev.x,
                y: ev.y,
            };
            tooltipRef.current.setState({
                item,
            });
            if (chartLegendRef.current) {
                chartLegendRef.current.setState({
                    currentTooltipItem: item,
                });
            }

        }
    };

    const onTooltipShow = (): void => {
        if (tooltipRef.current) {
            tooltipRef.current.setState({ isVisible: true });
        }
        if (chartLegendRef.current) {
            chartLegendRef.current.setState({ isTooltipVisible: true });
        }
    };

    const onTooltipChange = (ev: {
        tooltip: any;
        x: number;
        y: number;
        items: any;
    }): void => {
        if (tooltipRef.current) {
            tooltipRef.current.setState({
                item: {
                    ...ev.items[0],
                    x: get(tooltipRef.current.state.item.x, 'x', 10000),
                    y: get(tooltipRef.current.state.item.y, 'y', 10000),
                },
            });
        }
    };

    const debouncePlotMove = debounce(onPlotMove, 1);

    return (
        <BizCharts fallback={<Fragment />}>
            {({ Chart, Guide, Coord, Axis, Tooltip, Geom }) => (
                <Fragment>
                    <ChartContainerStyled data-name='chart-container'>

                        {
                            !props.isTooltipDisable &&
                            <ChartTooltip
                                chartRef={chartRef}
                                ref={tooltipRef}
                            />
                        }

                        <ChartHolderStyled ref={chartRef}>
                            <Chart
                                data={data
                                    .filter(item => item.value)
                                    // Маленькие значения приводятся к 1%, чтобы показывать на графике
                                    .map(item => ({
                                        ...item,
                                        value: item.value / total > .01 ? item.value : total / 100,
                                    }))
                                }
                                height={size}
                                padding={padding}
                                width={size}
                                onPlotMove={debouncePlotMove}
                                onTooltipChange={onTooltipChange}
                                onTooltipHide={onTooltipHide}
                                onTooltipShow={onTooltipShow}
                            >

                                <Coord innerRadius={innerRadius} radius={radius} type="theta" />
                                <Axis name="value" />
                                <Tooltip htmlContent={() => '<div></div>'} useHtml={true} />
                                <Guide>
                                    {
                                        !props.isTotalDisable &&
                                        <Guide.Html
                                            alignX="middle" alignY="middle"
                                            html={PieChartTitle(total, currency)}
                                            position={['50%', '50%']}
                                        />
                                    }
                                </Guide>
                                <Geom
                                    // doc https://bizcharts.net/products/bizCharts/api/geom#active
                                    //@ts-ignore
                                    active={[true, {
                                        highlight: false,
                                        style: {
                                            fillOpacity: 1,
                                            lineWidth: 1,
                                        },
                                    }]}
                                    color={['item', data.filter(item => item.value)
                                        .map((item: IChartPieItem): string => item.color)]}
                                    position="value"
                                    select={false}
                                    style={{
                                        fillOpacity: 1,
                                        lineWidth: 5,
                                        stroke: strokeColor,
                                    }}
                                    tooltip={['item', (item: string): {
                                        name: string;
                                        percent: string;
                                        value: string;
                                    } => {
                                        const value = chartData.find(i => i.item === item)?.value ?? 0;
                                        const percent = (value / total * 100);
                                        return {
                                            name: item,
                                            percent: percent < 1 ? '< 1%' : `${Math.round(percent)}%`,
                                            value: formatBalance(value) + ' ' + currency,
                                        };
                                    }]}
                                    type="intervalStack"
                                />

                            </Chart>
                        </ChartHolderStyled>

                        {
                            !props.isLegendDisable &&
                            <ChartLegend
                                currency={currency}
                                data={chartData.filter(item => item.value > 0)}
                                drop={drop}
                                ref={chartLegendRef}
                            />
                        }

                    </ChartContainerStyled>
                    {children}
                </Fragment>
            )}
        </BizCharts>
    );
};

export default memo<FC<IPieChartProps>>(PieChart);

